import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

// Core components
import { SFStack } from "../../grid";
import SFAccordion, {
  SFAccordionDetails,
  SFAccordionSummary,
} from "../../accordion";
import SFTypography from "../../typography";
import SFSkeleton from "../../skeleton";
import getStyled from "../../../hooks/getStyled";

// Custom components
import DurationFilterGroup from "./durationFilterGroup";
import RatingFilterGroup from "./ratingFilterGroup";
import DateFilterGroup from "./dateFilterGroup";
import TextFilterGroup from "./textFilterGroup";

// Other
import { FILTER_TYPES } from "./filterTypes";
import { getGridFilterValues } from "../../../services/dataGridService";
import getAdvancedQueryObject from "./getFilterModelObject";

const { styled } = getStyled();

const SFAccordionStyled = styled(SFAccordion)(({ theme }) => ({
  width: "100% !important",
  boxShadow: "none !important",
}));

const SFAccordionSummaryStyled = styled(SFAccordionSummary)(({ theme }) => ({
  flexDirection: "row-reverse",
  padding: "0px",
  gap: theme.spacing(1.25),
}));

const SFAccordionDetailsStyled = styled(SFAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(0, 0, 1.25, 3),
}));

const FilterGroup = ({
  filter,
  currentSelection,
  updateCurrentSelection,
  currentFilterModel = {},
  colNameTypes = {},
  datasource,
  disableAll,
  expandedFilterGroups,
  onFilterGroupToggle,
  externalColNameTypes,
  externalFilterSelection,
}) => {
  const { t } = useTranslation();

  const [options, setOptions] = useState({
    loading: datasource ? true : false,
    values: datasource?.values ?? [],
    counts: datasource?.valueCounts ?? [],
  });

  useEffect(() => {
    // This effect is used to fetch the counts for the current filter group options based on the current search text + advanced query filter
    // The counts are fetched from the backend using the same valuesUrl from the filter object
    let ignore = false;

    // Before calling the API to get the new counts, we need to create the filter model for the payload
    if (!datasource) {
      // Create a copy of the currentSelection object
      const updatedCurrentSelection = { ...currentSelection };
      // Clear the values for the current filter (counts for the current filter must be fetched in relation to the other filters)
      updatedCurrentSelection[filter.colName] = [];
      // Create the advanced query object for the current filter
      const advancedQueryObj = getAdvancedQueryObject(
        {...updatedCurrentSelection, ...externalFilterSelection},
        {...colNameTypes, ...externalColNameTypes},
      );
      // Create the advanced query filter object for the filter model
      const advancedQueryFilterObj = {
        filter: JSON.stringify(advancedQueryObj),
        filterType: "text",
        type: "contains",
      };
      // Create the filter model object for the payload
      const filterModel = {
        searchText: currentFilterModel.searchText,
        advancedQuery: advancedQueryFilterObj,
      };

      if (
        currentFilterModel.searchText &&
        typeof currentFilterModel.searchText === "object" &&
        !datasource
      ) {
        getGridFilterValues(filter.valuesUrl, {
          colName: filter.colName,
          filterModel,
        }).then((res) => {
          if (!ignore) {
            setOptions((prevOptions) => ({
              ...prevOptions,
              loading: false,
              values: res.data.values,
              counts: res.data.valueCounts,
            }));
          }
        });
      }
    } else {
      setOptions({
        loading: false,
        values: datasource?.values ?? [],
        counts: datasource?.valueCounts ?? [],
      });
    }
    return () => {
      ignore = true;
    };
  }, [
    filter.valuesUrl,
    filter.colName,
    JSON.stringify(currentFilterModel.advancedQuery),
    JSON.stringify(currentFilterModel.searchText),
    JSON.stringify(datasource),
  ]);

  return (
    <SFAccordionStyled
      disableGutters
      expanded={expandedFilterGroups.includes(filter.colName)}
      onChange={(event, isExpanded) =>
        onFilterGroupToggle(event, isExpanded, filter.colName)
      }
    >
      <SFAccordionSummaryStyled>
        <SFTypography variant="tableListSelected">{filter.label}</SFTypography>
      </SFAccordionSummaryStyled>
      <SFAccordionDetailsStyled>
        {options.loading ? (
          <SFSkeleton rows={5} showHeader={false} showFooter={false} />
        ) : options.values.length === 0 ? (
          // If there are no values, show a message
          <SFStack direction="row" alignItems="center" marginLeft={1.25}>
            <SFTypography variant="body1">
              {t("components.fullSearchDialog.noOptions")}
            </SFTypography>
          </SFStack>
        ) : filter.type === FILTER_TYPES.DURATION ? (
          <DurationFilterGroup
            colName={filter.colName}
            // No values prop here because the values are hardcoded
            counts={options.counts}
            currentSelection={currentSelection}
            updateCurrentSelection={updateCurrentSelection}
            defaultSelectedValues={filter.defaultSelectedValues}
            disableAll={disableAll}
          />
        ) : filter.type === FILTER_TYPES.RATING ? (
          <RatingFilterGroup
            colName={filter.colName}
            // No values prop here because the values are hardcoded
            counts={options.counts}
            currentSelection={currentSelection}
            updateCurrentSelection={updateCurrentSelection}
            defaultSelectedValues={filter.defaultSelectedValues}
            disableAll={disableAll}
          />
        ) : filter.type === FILTER_TYPES.DATE ? (
          <DateFilterGroup
            colName={filter.colName}
            // No values prop here because the values are hardcoded
            counts={options.counts}
            currentSelection={currentSelection}
            updateCurrentSelection={updateCurrentSelection}
            defaultSelectedValues={filter.defaultSelectedValues}
            disableAll={disableAll}
          />
        ) : (
          <TextFilterGroup
            colName={filter.colName}
            values={options.values}
            counts={options.counts}
            currentSelection={currentSelection}
            updateCurrentSelection={updateCurrentSelection}
            defaultSelectedValues={filter.defaultSelectedValues}
            search={filter.search}
            disableAll={disableAll}
            labelFormatter={filter.labelFormatter}
            comparator={filter.comparator}
            hideOption={filter.hideOption}
          />
        )}
      </SFAccordionDetailsStyled>
    </SFAccordionStyled>
  );
};

export default FilterGroup;

export const SHOW_MORE_COUNT = 4;
